import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Medal,
  Calendar,
  User,
  Award,
} from "lucide-react";

import { Button } from "../ui/button";
import { apiFetch } from "../../services/api/Awards";
import PageHeader from "../Header";

/* ================= TYPES ================= */

interface AwardItem {
  id: number;
  player: string;
  sport: string;
  year: string;
  age?: string | null;
  achievement?: string | null;
  image?: string | null;
}

interface PaginationMeta {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  from: number;
  to: number;
}

interface RangeGroup {
  title: string;
  data: AwardItem[];
  pagination: PaginationMeta | null;
}

/* ================= COMPONENT ================= */

export function RajivGandhiAwardsPage() {
  const { slug } = useParams<{ slug: string }>();

  const [rangeGroups, setRangeGroups] = useState<
    RangeGroup[]
  >([]);

  const [awardName, setAwardName] =
    useState("Awards");

  const [loading, setLoading] =
    useState(true);

  const [page, setPage] = useState(1);

  /* ================= FETCH ================= */

  const fetchAwards = async (
    pageNum = 1
  ) => {
    if (!slug) return;

    setLoading(true);

    try {
      const res = await apiFetch(
        `award/${slug}?page=${pageNum}`
      );

      console.log("✅ API RESPONSE:", res);

      const responseData = res?.data || {};

      /* AWARD INFO */
      const awardInfo =
        responseData.award;

      setAwardName(
        awardInfo?.name || "Awards"
      );

      /* RANGES */
      const ranges =
        responseData.ranges || {};

      /* CONVERT ALL RANGE DATA */
      const formattedRanges: RangeGroup[] =
        Object.entries(ranges).map(
          ([rangeTitle, rangeValue]: any) => ({
            title: rangeTitle,

            data:
              rangeValue?.data?.map(
                (item: any) => ({
                  id: item.id,
                  player:
                    item.player_name,
                  sport:
                    item.sport?.name ||
                    "N/A",
                  year:
                    item.year || "—",
                  age: item.age,
                  achievement:
                    item.achievement,
                  image:
                    item.image_url ||
                    item.image ||
                    "",
                })
              ) || [],

            pagination:
              rangeValue?.pagination ||
              null,
          })
        );

      setRangeGroups(
        formattedRanges
      );
    } catch (err) {
      console.error(err);
      setRangeGroups([]);
    } finally {
      setLoading(false);
    }
  };

  /* ================= EFFECT ================= */

  useEffect(() => {
    fetchAwards(page);
  }, [slug, page]);

  /* ================= HELPERS ================= */

  const stripHtml = (
    html?: string | null
  ) => {
    if (!html) return "";

    return html
      .replace(/<[^>]*>/g, "")
      .trim();
  };

  /* ================= UI ================= */

  return (
    <div className="w-full">

      {/* HEADER */}
      <PageHeader
        title={awardName}
        slug={slug}
      />

      {/* LOADING */}
      {loading && (
        <div className="py-20 text-center text-xl font-semibold text-gray-700">
          Loading awards...
        </div>
      )}

      {/* MAIN */}
      <div className="w-full min-h-screen py-12">

        {/* RANGE LOOP */}
        {!loading &&
          rangeGroups.map(
            (rangeGroup) => (
              <div
                key={rangeGroup.title}
                className="mb-20"
              >

                {/* RANGE HEADING */}
                <div className="flex items-center gap-4 px-5 mb-10">

                  <div className="h-[3px] flex-1 bg-gradient-to-r from-blue-900 to-transparent" />

                  <h2 className="text-5xl font-extrabold text-blue-900 whitespace-nowrap">
                    {
                      rangeGroup.title
                    }
                  </h2>

                  <div className="h-[3px] flex-1 bg-gradient-to-l from-blue-900 to-transparent" />

                </div>

                {/* GRID */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 px-5">

                  {rangeGroup.data.map(
                    (award) => (
                      <div
                        key={award.id}
                        className="
                          group
                          relative
                          bg-white
                          rounded-3xl
                          overflow-hidden
                          border
                          shadow-md
                          hover:shadow-2xl
                          transition-all
                          duration-300
                        "
                      >

                        {/* IMAGE */}
                        <div className="h-[450px] w-full overflow-hidden bg-gray-100">

                          <img
                            src={
                              award.image ||
                              ""
                            }
                            alt={
                              award.player
                            }
                            className="
                              h-full
                              w-full
                              object-cover
                              group-hover:scale-105
                              transition
                              duration-500
                            "
                          />

                        </div>

                        {/* INFO */}
                        <div className="p-5 text-center">

                          <h3 className="text-2xl font-bold text-blue-900 uppercase">
                            {
                              award.player
                            }
                          </h3>

                          <p className="text-gray-600 text-lg mt-2">
                            {
                              award.sport
                            }
                          </p>

                        </div>

                        {/* HOVER */}
                        <div
                          className="
                            absolute
                            inset-0
                            bg-blue-900/90
                            text-white
                            opacity-0
                            group-hover:opacity-100
                            transition
                            duration-300
                            flex
                            items-center
                            justify-center
                            p-6
                          "
                        >

                          <div className="text-center space-y-4">

                            <h3 className="text-3xl font-bold">
                              {
                                award.player
                              }
                            </h3>

                            <p className="flex justify-center items-center gap-2 text-lg">
                              <Medal className="w-5 h-5" />

                              {
                                award.sport
                              }
                            </p>

                            <p className="flex justify-center items-center gap-2 text-lg">
                              <Calendar className="w-5 h-5" />

                              Year:{" "}
                              {
                                award.year
                              }
                            </p>

                            {award.age && (
                              <p className="flex justify-center items-center gap-2 text-lg">
                                <User className="w-5 h-5" />

                                Age:{" "}
                                {
                                  award.age
                                }
                              </p>
                            )}

                            {award.achievement && (
                              <div className="flex justify-center items-center gap-2 text-sm leading-relaxed">

                                <Award className="w-5 h-5" />

                                <span>
                                  {stripHtml(
                                    award.achievement
                                  )}
                                </span>

                              </div>
                            )}

                          </div>

                        </div>
                      </div>
                    )
                  )}
                </div>

                {/* PAGINATION */}
                {rangeGroup.pagination &&
                  rangeGroup.pagination
                    .last_page >
                    1 && (
                    <div className="mt-10 px-5">

                      <div
                        className="
                          flex
                          flex-col
                          md:flex-row
                          justify-between
                          items-center
                          gap-5
                          bg-white
                          shadow-xl
                          rounded-3xl
                          p-5
                        "
                      >

                        {/* INFO */}
                        <div
                          className="
                            px-8
                            py-3
                            rounded-full
                            bg-gradient-to-r
                            from-blue-900
                            via-indigo-900
                            to-blue-700
                            text-white
                            font-semibold
                          "
                        >
                          Showing{" "}
                          {
                            rangeGroup
                              .pagination
                              .from
                          }{" "}
                          -{" "}
                          {
                            rangeGroup
                              .pagination
                              .to
                          }{" "}
                          of{" "}
                          {
                            rangeGroup
                              .pagination
                              .total
                          }{" "}
                          records
                        </div>

                        {/* BUTTONS */}
                        <div className="flex items-center gap-4">

                          {/* PREV */}
                          <Button
                            disabled={
                              page === 1
                            }
                            onClick={() =>
                              setPage(
                                page - 1
                              )
                            }
                            className="
                              rounded-full
                              px-6
                              py-2
                              bg-gradient-to-r
                              from-blue-900
                              via-indigo-900
                              to-blue-700
                            "
                          >
                            ← Prev
                          </Button>

                          {/* PAGE */}
                          <span
                            className="
                              px-6
                              py-2
                              rounded-full
                              bg-gradient-to-r
                              from-blue-900
                              via-indigo-900
                              to-blue-700
                              text-white
                              font-semibold
                            "
                          >
                            Page{" "}
                            {
                              rangeGroup
                                .pagination
                                .current_page
                            }{" "}
                            of{" "}
                            {
                              rangeGroup
                                .pagination
                                .last_page
                            }
                          </span>

                          {/* NEXT */}
                          <Button
                            disabled={
                              page ===
                              rangeGroup
                                .pagination
                                .last_page
                            }
                            onClick={() =>
                              setPage(
                                page + 1
                              )
                            }
                            className="
                              rounded-full
                              px-6
                              py-2
                              bg-gradient-to-r
                              from-blue-900
                              via-indigo-900
                              to-blue-700
                            "
                          >
                            Next →
                          </Button>

                        </div>
                      </div>
                    </div>
                  )}
              </div>
            )
          )}

        {/* NO DATA */}
        {!loading &&
          rangeGroups.length ===
            0 && (
            <div className="py-20 text-center text-gray-600 text-xl">
              No award data available.
            </div>
          )}
      </div>
    </div>
  );
}